import React from 'react';
import { Table, Skeleton, Flex } from 'antd';
import type { TableProps } from 'antd';

interface TeamsTableSkeletonProps {
  /** How many placeholder rows to render. */
  rows?: number;
}

type SkeletonRow = { key: string };

/**
 * Loading placeholder for {@link TeamsTable}. Renders the SAME Ant Design
 * `Table` shell so column widths, header and rounded border match the real
 * table exactly. Each cell mirrors the geometry of its real counterpart:
 *  - Team:        bold name line (140px)
 *  - Description: longer text line (300px)
 *  - Users:       three rounded user-name pills (matches `.org-user-badge`)
 *  - Access:      two pill badges + arrow button (matches `.org-access-cell`)
 *  - Actions:     two square icon-button blocks
 */
export function TeamsTableSkeleton({ rows = 8 }: TeamsTableSkeletonProps) {
  const dataSource: SkeletonRow[] = Array.from({ length: rows }).map((_, i) => ({
    key: `t-skeleton-${i}`,
  }));

  const columns: TableProps<SkeletonRow>['columns'] = [
    {
      title: ' ',
      key: 'name',
      width: 140,
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 90, minWidth: 90, height: 16 }} />
      ),
    },
    {
      title: ' ',
      key: 'description',
      width: 300,
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 220, minWidth: 220, height: 16 }} />
      ),
    },
    {
      title: ' ',
      key: 'users',
      width: 200,
      render: () => (
        <Flex gap={6} wrap="wrap">
          <Skeleton.Button active size="small" shape="round" style={{ width: 70, minWidth: 70, height: 22 }} />
          <Skeleton.Button active size="small" shape="round" style={{ width: 90, minWidth: 90, height: 22 }} />
          <Skeleton.Button active size="small" shape="round" style={{ width: 60, minWidth: 60, height: 22 }} />
        </Flex>
      ),
    },
    {
      title: ' ',
      key: 'access',
      width: 300,
      render: () => (
        <Flex align="center" gap={6} wrap="nowrap">
          <Skeleton.Button active size="small" shape="round" style={{ width: 124, minWidth: 124, height: 26 }} />
          <Skeleton.Button active size="small" shape="round" style={{ width: 124, minWidth: 124, height: 26 }} />
          <Skeleton.Avatar active size={20} shape="square" />
        </Flex>
      ),
    },
    {
      title: ' ',
      key: 'actions',
      width: 80,
      render: () => (
        <Flex gap={4}>
          <Skeleton.Avatar active size={20} shape="square" />
          <Skeleton.Avatar active size={20} shape="square" />
        </Flex>
      ),
    },
  ];

  return (
    /* The loading state wears the table box and its footer, so it does not
       jump a border when the data lands. */
    <div className="table-box">
      <Table<SkeletonRow>
      dataSource={dataSource}
      columns={columns}
      rowKey="key"
      pagination={false}
      scroll={{ x: 1020 }}
      aria-busy
      />
      <div className="table-footer">
        <Skeleton.Input active size="small" style={{ width: 150, height: 16 }} />
      </div>
    </div>
  );
}
